import { useCallback, useRef } from 'react'

import { useBoundStore } from '../store/store'
import type { Game } from '../types/game'
import { api } from '../utils/api-client'
import { fetchRecentPlayed } from '../utils/recentPlayed'

// The store buckets games by category only, but requests also vary by
// platform filter — two overlapping fetches for the same category must not
// let the older response win. Module-level (not per hook instance) so every
// consumer competes on the same marker.
const latestRequestByCategory = new Map<string, number>()
let requestSeq = 0

// Subscription-free loader. Reads allGames/categories imperatively inside the
// callback instead of subscribing, so consumers that only trigger loads (the
// globally-mounted BottomNav, game page, casino drawer) don't re-render on
// every games-list update. Consumers that render the loading state should use
// useGames below.
export const useLoadMoreGames = () => {
  const username = useBoundStore(state => state.user.username)
  const setCategoryGames = useBoundStore(state => state.casino.setCategoryGames)
  const setCategoryCount = useBoundStore(state => state.casino.setCategoryCount)
  const setLoadMoreLoading = useBoundStore(
    state => state.casino.setLoadMoreLoading,
  )

  const inFlightRef = useRef<Set<string>>(new Set())

  const loadMoreGames = useCallback(
    async (categoryKey: string, platform: string, fresh: boolean) => {
    if (categoryKey === 'favorites' || (!username && categoryKey === 'recent_played')) return

      const requestKey = `${categoryKey}|${platform}|${fresh ? 1 : 0}`
      if (inFlightRef.current.has(requestKey)) return
      inFlightRef.current.add(requestKey)

      // recent_played is served by its own endpoint, which returns the FULL
      // game list (with the count/providers) in a single response — no
      // pagination and no provider filter. Use it directly instead of the
      // paginated games_v2 catalog: games_v2 additionally drops the recent list
      // for display_platform=0 (web), whereas this endpoint always returns it.
      // The fetch goes through the shared fetchRecentPlayed request so this
      // hook's per-instance guard above (which can't see the bottom-nav's own
      // instance, nor the category-list's count fetch) doesn't leave duplicate
      // GETs on the wire.
      if (categoryKey === 'recent_played') {
        const requestId = ++requestSeq
        latestRequestByCategory.set(categoryKey, requestId)
        setLoadMoreLoading(true)
        try {
          const data = await fetchRecentPlayed(username)
          // Drop the response unless it's still the latest request AND the
          // account it was issued for is still signed in. Recent games are
          // account-private history: a logout/account-switch while this was in
          // flight must never leak one account's history into the next session.
          const stillCurrent =
            latestRequestByCategory.get(categoryKey) === requestId &&
            useBoundStore.getState().user.username === username
          // Whole list arrives at once, so always replace (never append). The
          // same response carries the live count, so refresh the tab badge too
          // (it goes stale after the user plays a new game between visits).
          if (stillCurrent) {
            // Whole list arrives in one response — a single page, not part of
            // the paginated games_v2 sequence below.
            setCategoryGames(categoryKey, data?.games ?? [], true, 1)
            if (typeof data?.count === 'number') {
              setCategoryCount(categoryKey, data.count)
            }
          }
        } catch (error) {
          console.error(error)
        } finally {
          inFlightRef.current.delete(requestKey)
          if (latestRequestByCategory.get(categoryKey) === requestId) {
            setLoadMoreLoading(false)
          }
        }
        return
      }

      // Read fresh state at call time (not via subscription — see note above).
      const { allGames, categories, loadedPage } =
        useBoundStore.getState().casino

      const totalCount = Number(
        platform
          ? categories?.find(c => c.category === categoryKey)?.providers?.[
              platform
            ]
          : categories?.find(c => c.category === categoryKey)?.count,
      )

      if (allGames?.[categoryKey]?.length >= totalCount && !fresh) {
        inFlightRef.current.delete(requestKey)
        return
      }

      // The page the server last returned for this category, not the cached
      // list divided by an assumed page size. That division was hardcoded to
      // 45 while the server was returning 40, so from the ninth page on it
      // asked for one it already held — and setCategoryGames concatenates
      // without deduping, so the grid gained forty duplicate tiles and
      // stopped advancing. An empty cache still restarts at page 1, whatever
      // the counter says.
      const currentPage =
        fresh || !allGames?.[categoryKey]?.length
          ? 0
          : loadedPage?.[categoryKey] ?? 0
      const requestId = ++requestSeq
      latestRequestByCategory.set(categoryKey, requestId)
      setLoadMoreLoading(true)
      try {
        const res = await api.get<Game[]>(
          `/data/games_v2.php?category=${categoryKey}&provider=${
            platform ?? ''
          }&account=${username}&page=${currentPage + 1}`,
        )
        // Drop a superseded response (e.g. provider filter A resolving after
        // filter A,B) instead of overwriting the newer request's games.
        if (latestRequestByCategory.get(categoryKey) === requestId) {
          setCategoryGames(categoryKey, res.data, fresh, currentPage + 1)
        }
      } catch (error) {
        console.error(error)
      } finally {
        inFlightRef.current.delete(requestKey)
        // A superseded request must not clear the newer request's spinner.
        if (latestRequestByCategory.get(categoryKey) === requestId) {
          setLoadMoreLoading(false)
        }
      }
    },
    [username, setLoadMoreLoading, setCategoryGames, setCategoryCount],
  )

  return loadMoreGames
}

const useGames = () => {
  const loadMoreGames = useLoadMoreGames()
  const loadMoreLoading = useBoundStore(state => state.casino.loadMoreLoading)

  return {
    loadMoreGames,
    loadMoreLoading,
  }
}

export default useGames
